import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ErrorLog } from '../../types/admin/AdminTypes';

interface ErrorsTableProps {
  errors: ErrorLog[];
  onResolve?: (errorId: string) => void;
  onViewDetails?: (error: ErrorLog) => void;
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('pt-BR');
};

const ErrorsTable: React.FC<ErrorsTableProps> = ({
  errors,
  onResolve,
  onViewDetails,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ErrorOutlineIcon sx={{ color: '#f44336' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Erros Recentes
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Mensagem</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.map((error) => (
              <TableRow key={error.id}>
                <TableCell>
                  <Chip
                    label={error.errorType}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {error.message}
                </TableCell>
                <TableCell>{error.userName || '-'}</TableCell>
                <TableCell>{formatDate(error.occurredAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={error.resolved ? 'Resolvido' : 'Pendente'}
                    color={error.resolved ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Ver detalhes">
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails?.(error)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!error.resolved && (
                      <Tooltip title="Marcar como resolvido">
                        <IconButton
                          size="small"
                          onClick={() => onResolve?.(error.id)}
                          color="success"
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(page - 1, 0)}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Linhas por página"
      />

      {errors.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Nenhum erro registrado 🎉
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ErrorsTable;
